"use client";

import { useState, useCallback } from "react";
import { Check, MessageSquare } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { useClientGroups } from "@/query/hooks/useClientGroups";
import { useTranslation } from "react-i18next";
import { OptionButton } from "./OptionButton";
import { PartnerField } from "./PartnerField";
import { GroupField } from "./GroupField";
import {
  extractTargetName,
  extractRecommendedLevel,
  extractRecommendedScope,
  type PricingScopeData,
} from "./types";

export function PricingScopeCard({
  data,
  onConfirm,
}: {
  readonly data: PricingScopeData;
  readonly onConfirm: (
    level: string,
    scope: string,
    targetId: string | null,
  ) => void;
}) {
  const { t } = useTranslation();
  const targetName = extractTargetName(data);
  const [selectedLevel, setSelectedLevel] = useState(
    extractRecommendedLevel(data),
  );
  const [selectedScope, setSelectedScope] = useState(
    extractRecommendedScope(data),
  );
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    data.target_id ?? null,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    data.partner_groups?.[0]?.id ?? null,
  );

  const { data: allGroups } = useClientGroups();

  let resolvedTargetId: string | null = null;
  if (selectedLevel === "partner") resolvedTargetId = selectedPartnerId;
  else if (selectedLevel === "group") resolvedTargetId = selectedGroupId;

  const canConfirm =
    selectedLevel === "base" ||
    (selectedLevel === "partner" && !!selectedPartnerId) ||
    (selectedLevel === "group" && !!selectedGroupId);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedLevel, selectedScope, resolvedTargetId);
  }, [selectedLevel, selectedScope, resolvedTargetId, onConfirm]);

  return (
    <div
      className="rounded-lg border border-violet-200 bg-violet-50/30 dark:border-violet-800 dark:bg-violet-900/10 overflow-hidden"
      data-testid="user-input-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <MessageSquare className="h-4 w-4 text-violet-600 dark:text-violet-400 shrink-0" />
        <span className="text-sm font-medium">
          {t(
            "ai.userInput.pricingScopeTitle",
            "Confirmation du niveau de prix",
          )}
        </span>
      </div>

      {/* Level options */}
      <div className="px-3 pb-2">
        <div className="text-xs text-muted-foreground mb-1.5">
          {t("ai.userInput.level", "Niveau")}
        </div>
        <div className="flex flex-wrap gap-2">
          {data.level_options.map((opt) => (
            <OptionButton
              key={opt.value}
              option={opt}
              selected={selectedLevel === opt.value}
              onClick={() => setSelectedLevel(opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Partner selector */}
      {selectedLevel === "partner" && (
        <PartnerField
          partnerId={selectedPartnerId}
          partnerName={targetName}
          onChange={setSelectedPartnerId}
        />
      )}

      {/* Group selector */}
      {selectedLevel === "group" && (
        <GroupField
          groupId={selectedGroupId}
          groups={allGroups ?? []}
          onChange={setSelectedGroupId}
        />
      )}

      {/* Scope options */}
      <div className="px-3 pb-2">
        <div className="text-xs text-muted-foreground mb-1.5">
          {t("ai.userInput.scope", "Scope")}
        </div>
        <div className="flex flex-wrap gap-2">
          {data.scope_options.map((opt) => (
            <OptionButton
              key={opt.value}
              option={opt}
              selected={selectedScope === opt.value}
              onClick={() => setSelectedScope(opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Confirm button */}
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-violet-200/50 dark:border-violet-800/50 bg-background/40">
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="h-7 gap-1.5 text-xs"
          data-testid="user-input-confirm"
        >
          <Check className="h-3 w-3" />
          {t("ai.userInput.confirm", "Confirmer")}
        </Button>
      </div>
    </div>
  );
}
